import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useRef, useEffect } from 'react';
import { deleteAgentChats } from './agentsChatsReducer';
import { useAiAgentApiFacade } from '../../apis/facades/useAiAgentApiFacade';
import { selectDefaultCreationCrmOrgId } from '../../reducers/userInfoReducer';
import { telemetry } from '../../telemetry';
import { useSweepNotifications } from '../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../reducers/notificationsReducer';
import {
  addAgent,
  addAgentFiles as addAgentFilesAction,
  deleteAgent as deleteAgentAction,
  deleteAgentFile,
  renameAgent as renameAgentAction,
  setAgentUserInstructions,
  setAgentSystemInstructions,
  setAgentIssues,
  setAgents,
  setAgentsLoading,
  updateAgentIssueStatus,
  setIsLoadingIssues,
  addAgentBestPractices as addAgentBestPracticesAction,
  removeAgentBestPractices as removeAgentBestPracticesAction,
} from './agentsReducer';
import { selectAgents, selectAgentsIsLoading } from './agentsSelectors';
import { AgentContextComponent, AgentType } from '@server/ai';
import { AI_AGENTS_INFO, AgentFile } from './aiAgentsConsts';
import { IssueStatus } from '@server/issues';

const useAiAgents = () => {
  const {
    get_agents,
    rename_agent,
    delete_agent,
    create_agent,
    edit_agent_user_instructions,
    edit_system_instructions,
    request_file_upload_url,
    upload_file_to_url,
    confirm_file_upload,
    delete_agent_file,
    update_issueStatus,
    get_issues,
    add_best_practices_to_agent,
    remove_best_practices_from_agent,
    patch_agent_configurations,
  } = useAiAgentApiFacade();
  const { addNotification } = useSweepNotifications();
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const _crmOrgId = crmOrgId ?? undefined;
  const agents = useSelector(selectAgents(_crmOrgId));

  //need to use ref because putting "agents" directly in the dep array of "fetchAgentIssues" is causing endless renders
  const agentsRef = useRef(agents);
  useEffect(() => {
    agentsRef.current = agents;
  }, [agents]);

  const agentsIsLoading = useSelector(selectAgentsIsLoading(_crmOrgId));

  const dispatch = useDispatch();

  const fetchAiAgents = useCallback(async () => {
    if (!crmOrgId) return;
    try {
      dispatch(setAgentsLoading({ crmOrgId }));
      const agents = await get_agents();
      const orderedAgents = agents.toSorted((a) => (a.type === AgentType.Documentation ? -1 : 1));
      dispatch(
        setAgents({
          crmOrgId,
          agents: orderedAgents,
        }),
      );
    } catch (e) {
      telemetry.captureError(e);
    }
  }, [crmOrgId, dispatch, get_agents]);

  const renameAgent = useCallback(
    async (agentId: string, name: string) => {
      if (!crmOrgId) return;
      try {
        await rename_agent(agentId, { name });
        dispatch(renameAgentAction({ crmOrgId, agentId, name }));
      } catch (e) {
        telemetry.captureError(e);
      }
    },
    [crmOrgId, dispatch, rename_agent],
  );
  const editAgentUserInstructions = useCallback(
    async (agentId: string, userInstructions: string) => {
      if (!crmOrgId) return;
      try {
        await edit_agent_user_instructions(agentId, { userInstructions });
        dispatch(setAgentUserInstructions({ crmOrgId, agentId, userInstructions }));
      } catch (e) {
        telemetry.captureError(e);
      }
    },
    [crmOrgId, dispatch, edit_agent_user_instructions],
  );

  const editAgentSystemInstructions = useCallback(
    async (agentId: string, systemInstructions: string) => {
      if (!crmOrgId) return;
      try {
        await edit_system_instructions(agentId, { systemInstructions });
        dispatch(setAgentSystemInstructions({ crmOrgId, agentId, systemInstructions }));
      } catch (e) {
        telemetry.captureError(e);
      }
    },
    [crmOrgId, dispatch, edit_system_instructions],
  );
  const addAgentFiles = useCallback(
    async (agentId: string, files: File[]) => {
      if (!crmOrgId) return;

      const uploadedFiles: AgentFile[] = [];
      await Promise.all(
        files.map(async (file) => {
          try {
            // Extract file name and type
            const pointIndex = file.name.lastIndexOf('.');
            const fileName = pointIndex !== -1 ? file.name.slice(0, pointIndex) : file.name;
            const fileType = pointIndex !== -1 ? file.name.slice(pointIndex + 1) : '';
            const contentType = file.type || 'application/octet-stream';

            const { uploadUrl, fileKey } = await request_file_upload_url(agentId, {
              fileName,
              fileType,
              contentType,
            });

            await upload_file_to_url(uploadUrl, file, contentType);

            const confirmedFile = await confirm_file_upload(agentId, {
              fileKey,
              fileName,
              fileType,
            });

            uploadedFiles.push(confirmedFile);
          } catch (e) {
            addNotification({
              message: `Failed to upload file: ${file.name}`,
              variant: SweepNotificationVariant.Error,
            });
            telemetry.captureError(e, {
              fileName: file.name,
              agentId,
            });
          }
        }),
      );

      if (uploadedFiles.length > 0) {
        dispatch(addAgentFilesAction({ crmOrgId, agentId, files: uploadedFiles }));
      }
    },
    [
      crmOrgId,
      dispatch,
      request_file_upload_url,
      upload_file_to_url,
      confirm_file_upload,
      addNotification,
    ],
  );
  const deleteFile = useCallback(
    async (agentId: string, fileId: string) => {
      if (!crmOrgId) return;
      try {
        await delete_agent_file(agentId, fileId);
        dispatch(deleteAgentFile({ crmOrgId, agentId, fileId }));
      } catch (e) {
        telemetry.captureError(e);
      }
    },
    [crmOrgId, delete_agent_file, dispatch],
  );
  const deleteAgent = useCallback(
    async (agentId: string) => {
      if (!crmOrgId) return;
      try {
        dispatch(deleteAgentAction({ crmOrgId, agentId }));
        dispatch(deleteAgentChats({ agentId }));

        await delete_agent(agentId);
      } catch (e) {
        telemetry.captureError(e);
      }
    },
    [crmOrgId, dispatch, delete_agent],
  );

  const createAgent = useCallback(
    async ({
      name,
      type,
      contextComponents,
      instructions,
      bestPracticeIds,
      metadataElements,
    }: {
      name: string;
      instructions?: string;
      type: AgentType;
      contextComponents: AgentContextComponent;
      bestPracticeIds?: string[];
      metadataElements?: { name: string; type: string }[];
    }) => {
      if (!crmOrgId) return;
      try {
        const agent = await create_agent({
          name,
          type,
          contextComponents,
          userInstructions: instructions ?? null,
          bestPracticeIds,
          metadataElements,
        });
        dispatch(
          addAgent({
            crmOrgId,
            agent,
          }),
        );
        return agent;
      } catch (e) {
        telemetry.captureError(e);
      }
    },
    [crmOrgId, create_agent, dispatch],
  );

  const updateIssueStatus = useCallback(
    async ({
      agentId,
      issueId,
      newStatus,
    }: {
      agentId: string;
      issueId: string;
      newStatus: IssueStatus;
    }) => {
      if (!crmOrgId) return;
      try {
        await update_issueStatus({
          agentId,
          issueId,
          newStatus,
        });
        dispatch(
          updateAgentIssueStatus({
            crmOrgId,
            agentId,
            issueId,
            newStatus,
          }),
        );
        return true;
      } catch (e) {
        telemetry.captureError(e);
      }
    },
    [crmOrgId, dispatch, update_issueStatus],
  );

  const fetchAgentIssues = useCallback(
    async (agentId: string) => {
      const agent = agentsRef.current?.find((a) => a.id === agentId);
      if (!agent) return;
      const agentInfo = AI_AGENTS_INFO[agent.type];
      if (agentInfo.isSupportIssues && !agent.issues && !agent.isLoadingIssues) {
        if (crmOrgId) {
          dispatch(
            setIsLoadingIssues({
              crmOrgId,
              agentId,
            }),
          );
          try {
            const data = await get_issues(agentId);
            const { issues, activeScan } = data;
            dispatch(
              setAgentIssues({
                crmOrgId,
                agentId,
                issues,
                activeScan,
              }),
            );
          } catch (e) {
            telemetry.captureError(e);
          }
        }
      }
    },
    [crmOrgId, dispatch, get_issues],
  );

  const refreshAgentIssues = useCallback(
    async (agentId: string) => {
      const agent = agentsRef.current?.find((a) => a.id === agentId);
      if (!agent) return;
      const agentInfo = AI_AGENTS_INFO[agent.type];
      if (agentInfo.isSupportIssues && crmOrgId) {
        dispatch(
          setIsLoadingIssues({
            crmOrgId,
            agentId,
          }),
        );
        try {
          const data = await get_issues(agentId);
          const { issues, activeScan } = data;
          dispatch(
            setAgentIssues({
              crmOrgId,
              agentId,
              issues,
              activeScan,
            }),
          );
        } catch (e) {
          telemetry.captureError(e);
        }
      }
    },
    [crmOrgId, dispatch, get_issues],
  );

  const addAgentBestPractices = useCallback(
    async (agentId: string, bestPracticeIds: string[]) => {
      if (!crmOrgId) return;
      try {
        await add_best_practices_to_agent(agentId, bestPracticeIds);
        dispatch(addAgentBestPracticesAction({ crmOrgId, agentId, bestPracticeIds }));
      } catch (e) {
        telemetry.captureError(e);
      }
    },
    [crmOrgId, dispatch, add_best_practices_to_agent],
  );

  const removeAgentBestPractices = useCallback(
    async (agentId: string, bestPracticeIds: string[]) => {
      if (!crmOrgId) return;
      try {
        await remove_best_practices_from_agent(agentId, bestPracticeIds);
        dispatch(removeAgentBestPracticesAction({ crmOrgId, agentId, bestPracticeIds }));
      } catch (e) {
        telemetry.captureError(e);
      }
    },
    [crmOrgId, dispatch, remove_best_practices_from_agent],
  );

  const updateAgentConfigurations = useCallback(
    async (agentId: string, includeConfigurations: { name: string; type: string }[]) => {
      if (!crmOrgId) return;
      try {
        const updatedAgent = await patch_agent_configurations(agentId, { includeConfigurations });
        // Refresh agents list to get updated agent
        await fetchAiAgents();
        return updatedAgent;
      } catch (e) {
        telemetry.captureError(e);
      }
    },
    [crmOrgId, patch_agent_configurations, fetchAiAgents],
  );

  return {
    agents,
    fetchAiAgents,
    agentsIsLoading,
    renameAgent,
    deleteAgent,
    createAgent,
    editAgentUserInstructions,
    editAgentSystemInstructions,
    addAgentFiles,
    deleteFile,
    updateIssueStatus,
    fetchAgentIssues,
    refreshAgentIssues,
    addAgentBestPractices,
    removeAgentBestPractices,
    updateAgentConfigurations,
  };
};

export default useAiAgents;
